import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Status } from '../../../../../../shared/builds/types';
import Locator from '../../../../../../shared/Locator';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { addAndFetchBuildWatchRevision } from '../../../../../redux/dependenciesV2/actions';
import List from '../../../../generic/List';
import PartialResultGate from '../../../generic/PartialResult/PartialResultGate';
import type { Dependency, DependencyIssue } from '../../../../../../shared/dependenciesV2';
import type { DependencyV2Modals } from '../../hooks/useDependencyModals';

import DependencyActionDropdown from './DependencyActionDropdown';
import DependencyTitle from './DependencyTitle/DependencyTitle';
import DetailsRow from './DetailsRow';
import { getIsAnalyzing, getStatusFromDependencyState, isDependencyBlocked } from './helpers';
import StateContextPanel from './StateContextPanel';

import styles from './DependencyRow.module.less';

type Props = {
  dependency: Dependency;
  modals: DependencyV2Modals;
  setSelectedDependencyRevisionLocator: (locator: string) => void;
  removeDependency: (locator: string, isManual: boolean) => void;
  setDependencyLocatorToEdit: (locator: string | null) => void;
  setInProgressBuildId: (buildId: number | null) => void;
  handleOverwriteManualDependency: (overwriteDependencyLocator: string) => void;
};

const DependencyTableRow: VoidFunctionComponent<Props> = ({
  dependency,
  modals,
  setSelectedDependencyRevisionLocator,
  removeDependency,
  setDependencyLocatorToEdit,
  setInProgressBuildId,
  handleOverwriteManualDependency,
}) => {
  const dispatch = useDispatch();
  const isProjectScope = useSelector(DependenciesV2Store.selectors.getIsProjectScope);
  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);
  const revisionInstance = useSelector(DependenciesV2Store.selectors.getBuildWatchRevision(dependency.locator));
  // Memo-ize this to prevent the Locator object from changing, triggering downstream actions
  const locator = useMemo(() => new Locator(dependency.locator), [dependency.locator]);
  const status = getStatusFromDependencyState(dependency.status);
  const isBlocked = isDependencyBlocked(dependency.issues as DependencyIssue[]);
  const isManual = dependency.isManual;
  const isSnippetConversion = dependency.isSnippetConversion;
  const isVendored = dependency.vendoredPaths.length !== 0;
  const isOrgMember = useSelector(DependenciesV2Store.selectors.getIsOrgMember);
  const hasMalwareIssue = dependency.issues.some((issue) => Boolean(issue.malwareReportName));

  // Load revision build data on start if the status is not analyzed
  useEffect(() => {
    if (status !== Status.Analyzed && status !== Status.Unknown) {
      // Load for all non-analyzed revisions, but only watch revisions that are in progress
      const shouldWatch = status === Status.InProgress;
      dispatch(addAndFetchBuildWatchRevision(dependency.locator, shouldWatch));
    }
    // Only include revision, because we don't want to reload data if the status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependency.locator]);

  // Stop watching the revision if there are no more running builds
  useEffect(() => {
    if (!revisionInstance) {
      return;
    }

    // If the dependency isn't analyzing, stop watching it. Only watch in-progress builds
    if (!getIsAnalyzing(revisionInstance?.builds ?? [])) {
      dispatch(DependenciesV2Store.actions.removeRevisionFromBuildWatch(dependency.locator));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionInstance]);

  const dependencyTitleClick = () => {
    setSelectedDependencyRevisionLocator(dependency.locator);
  };

  return (
    <List.RowContainer>
      <List.Row
        hasTightPadding
        onClick={dependencyTitleClick}
      >
        <div className={styles.DependencyRow}>
          <div className={styles.DependencyInfo}>
            <DependencyTitle
              title={dependency.title}
              revision={locator}
              version={dependency.version}
              depth={dependency.depth}
              layerDepth={dependency.layerDepth}
              isBlocked={isBlocked}
              isManual={isManual}
              isVendored={isVendored}
              isSnippetConversion={isSnippetConversion}
              hasMalwareIssue={hasMalwareIssue}
              onClick={dependencyTitleClick}
              modals={modals}
            />
            {(status === Status.Analyzed || isReleaseGroupScope) && (
              <DetailsRow dependency={dependency} />
            )}
            {status !== Status.Analyzed &&
              <PartialResultGate>
                <StateContextPanel
                  locator={dependency.locator}
                  status={dependency.status}
                  setInProgressBuildId={setInProgressBuildId}
                />
              </PartialResultGate>}
          </div>
          {isOrgMember && (
            <PartialResultGate>
              <div className={styles.DependencyRowPullRight}>
                <DependencyActionDropdown
                  dependency={dependency}
                  status={status}
                  removeDependency={removeDependency}
                  setDependencyLocatorToEdit={setDependencyLocatorToEdit}
                  setInProgressBuildId={setInProgressBuildId}
                  isProjectScope={isProjectScope}
                  handleOverwriteManualDependency={handleOverwriteManualDependency}
                />
              </div>
            </PartialResultGate>
          )}
        </div>
      </List.Row>
    </List.RowContainer>
  );
};

export default DependencyTableRow;
